import { Component, useState } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Home,
  LifeBuoy,
  RefreshCw,
  RotateCcw,
} from 'lucide-react';
import { Sentry } from '@/lib/sentry';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Alert, Button, Card } from '@/components/ds';
import { reportCrash } from '@/api/feedback.api';
import { captureContext } from '@/lib/feedback-capture';
import { useAuth } from '@/contexts/AuthContext';
import { dashboardRoutePaths } from '@/routes/route-config';
import {
  isChunkLoadError,
  tryRecoverFromChunkError,
} from '@/lib/chunk-recovery';
import { ChunkRecoveryScreen } from './AppErrorBoundary';

/**
 * The per-route error boundary.
 *
 * ── Why this exists ─────────────────────────────────────────
 * `AppErrorBoundary` sits ABOVE the router, so a render error in ONE page
 * blanked the entire application: the user lost the sidebar, the topbar and
 * every other route, and the only way out of `/dashboard/finance/profit-loss`
 * throwing was to retype a URL. One bad API payload took the whole app down.
 *
 * This boundary mounts per route, INSIDE the router, so a page that throws
 * takes down only itself. Its fallback renders inside the app shell, which
 * means the nav is still there and the user can just click somewhere else.
 *
 * `AppErrorBoundary` stays where it is as the last resort: it catches what
 * happens above the router (providers, the shell) and anything this
 * boundary's own fallback manages to throw.
 *
 * ── Resetting ───────────────────────────────────────────────
 * React never clears a boundary on its own. `<Routes>` renders the same
 * component type at the same tree position for most navigations, so React
 * REUSES this instance rather than remounting it — without the pathname reset
 * key below, one broken page would keep showing its error card on every route
 * the user visited afterwards.
 */

/**
 * What the fallback renders around the error card.
 *
 * - `shell`    — inside `DashboardLayout`, so the nav survives the error.
 * - `page`     — on its own, centred. For routes with no shell to return to.
 * - `inline`   — bare card, for a boundary nested inside a layout that is
 *                already painted (the settings sections).
 */
export type ErrorChrome = 'shell' | 'page' | 'inline';

/** Routes that deliberately render without the app shell (kiosk, print). */
const CHROMELESS_PREFIXES = ['/print/', '/queue/display/'];

const isChromelessPath = (pathname: string) =>
  CHROMELESS_PREFIXES.some((prefix) => pathname.startsWith(prefix));

/* ── Fallback UI ──────────────────────────────────────────── */

interface ErrorCardProps {
  error: Error;
  eventId: string | null;
  componentStack: string | null;
  onRetry: () => void;
  chrome: ErrorChrome;
}

function ErrorCard({
  error,
  eventId,
  componentStack,
  onRetry,
  chrome,
}: ErrorCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // `page` chrome means there is no nav around this card — a print sheet, the
  // login screen. Promising a menu that isn't there, and offering a dashboard
  // link that a signed-out visitor gets bounced off, both mislead.
  const hasNav = chrome !== 'page';

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col gap-4 py-8">
      <Card>
        <Card.Body className="flex flex-col items-center gap-6 p-6 text-center md:p-8">
          <div
            className="flex size-14 items-center justify-center rounded-full bg-danger-50"
            aria-hidden
          >
            <AlertTriangle className="size-7 text-danger-600" />
          </div>

          <div className="space-y-2">
            <h1 className="text-h2 text-foreground">
              {t('errors.page.title')}
            </h1>
            <p className="text-body text-muted">
              {hasNav
                ? t('errors.page.description')
                : t('errors.page.descriptionStandalone')}
            </p>
          </div>

          {/* Retry leads: most of these are one transient bad payload, and a
              re-render is cheaper — and keeps more state — than a reload. */}
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-center">
            <Button
              onPress={onRetry}
              startIcon={<RotateCcw className="size-4" />}
            >
              {t('errors.page.retry')}
            </Button>
            <Button
              variant="secondary"
              onPress={() => window.location.reload()}
              startIcon={<RefreshCw className="size-4" />}
            >
              {t('errors.page.reload')}
            </Button>
            {hasNav && (
              <Button
                variant="ghost"
                onPress={() => navigate(dashboardRoutePaths.home)}
                startIcon={<Home className="size-4" />}
              >
                {t('errors.page.goHome')}
              </Button>
            )}
          </div>

          <ReportIssue
            error={error}
            eventId={eventId}
            componentStack={componentStack}
          />
        </Card.Body>
      </Card>
    </div>
  );
}

/**
 * One-click crash report to the Dentacares team.
 *
 * This screen replaced a "Technical details" panel that showed the raw
 * exception. A clinician cannot act on `TypeError: Cannot read properties of
 * undefined` — it only makes a recoverable state read as a crash — and asking
 * them to relay it by hand is worse. The stack, the route, the Sentry
 * reference and the browser build are attached automatically, so the button is
 * the whole interaction.
 *
 * What changed: this used to `POST /feature-requests` and file a ticket typed
 * `bug`. That put a machine-written row carrying a stack trace into the
 * clinic's own list of submissions — something they did not write, could not
 * act on, and which inflated their count — and it created one ticket per click,
 * so a single bad deploy could bury their real reports under identical rows.
 *
 * It now posts to the crash sink, which groups occurrences by a fingerprint
 * derived from the error and the route pattern. One recurring bug is one issue
 * for the team, and none of it appears on any clinic screen.
 */
function ReportIssue({
  error,
  eventId,
  componentStack,
}: {
  error: Error;
  eventId: string | null;
  componentStack: string | null;
}) {
  const { t, i18n } = useTranslation();
  const { user, clinic } = useAuth();
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'failed'>(
    'idle',
  );

  // The crash sink attributes the report to the reporter AND their clinic,
  // both read from the token. Without a session it is a 401, and before an
  // clinic is chosen there is no clinic to attribute it to — so on those
  // screens the button could only ever fail. Don't offer it.
  if (!user || !clinic) return null;

  const submit = async () => {
    setStatus('sending');
    const sent = await reportCrash({
      errorName: error.name || 'Error',
      errorMessage: error.message ?? '',
      stack: error.stack ?? undefined,
      componentStack: componentStack ?? undefined,
      sentryEventId: eventId ?? undefined,
      context: captureContext({
        role: user?.clinicRole,
        language: i18n.language,
      }),
    });
    setStatus(sent ? 'sent' : 'failed');
  };

  if (status === 'sent') {
    return (
      <div className="w-full border-t border-neutral-200 pt-6">
        <p className="inline-flex items-center gap-2 text-small font-semibold text-success-700">
          <CheckCircle2 className="size-4" aria-hidden />
          {t('errors.page.reported')}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-3 border-t border-neutral-200 pt-6">
      <p className="text-small text-muted">{t('errors.page.reportHint')}</p>
      {status === 'failed' && (
        <Alert tone="danger" live>
          {t('errors.page.reportFailed')}
        </Alert>
      )}
      <Button
        variant="secondary"
        size="sm"
        onPress={submit}
        isLoading={status === 'sending'}
        loadingText={t('errors.page.reporting')}
        startIcon={<LifeBuoy className="size-4" />}
      >
        {status === 'failed'
          ? t('errors.page.reportRetry')
          : t('errors.page.report')}
      </Button>
    </div>
  );
}

function RouteErrorFallback(card: ErrorCardProps) {
  if (card.chrome === 'inline') return <ErrorCard {...card} />;
  if (card.chrome === 'page') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-0 px-4">
        <ErrorCard {...card} />
      </div>
    );
  }
  return (
    <DashboardLayout>
      <ErrorCard {...card} />
    </DashboardLayout>
  );
}

/* ── The boundary ─────────────────────────────────────────── */

interface Props {
  children: ReactNode;
  /** What the fallback renders around the card. */
  chrome: ErrorChrome;
  /** Changing this clears the error — the current pathname, in practice. */
  resetKey: string;
  /** Clears React Query's errored queries so a retry actually refetches. */
  onReset: () => void;
}

interface State {
  error: Error | null;
  eventId: string | null;
  /** Kept so "Report this issue" can attach it; never shown on screen. */
  componentStack: string | null;
  /** A stale-chunk error self-heals; show a neutral screen, not a crash. */
  recovering: boolean;
  resetKey: string;
}

const CLEARED = {
  error: null,
  eventId: null,
  componentStack: null,
  recovering: false,
} as const;

class RouteErrorBoundaryInner extends Component<Props, State> {
  state: State = { ...CLEARED, resetKey: this.props.resetKey };

  static getDerivedStateFromProps(props: Props, state: State): State | null {
    if (props.resetKey === state.resetKey) return null;
    return { ...CLEARED, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // A new build shipped while the tab was open, so this page's chunk is
    // gone. That is not a page bug and must not render as one — hand it to
    // the shared one-shot recovery, which reloads onto the new build.
    if (isChunkLoadError(error)) {
      return { error, recovering: tryRecoverFromChunkError() };
    }
    return { error, recovering: false };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (typeof window === 'undefined' || isChunkLoadError(error)) return;
    // No `route` tag: the pathname carries record ids (`/patients/<uuid>`),
    // which would both leak them into a searchable tag and blow up its
    // cardinality. Sentry records the URL on the event either way.
    const eventId = Sentry.captureException(error, {
      tags: { boundary: 'route' },
      extra: { componentStack: info.componentStack },
    });
    this.setState({ eventId, componentStack: info.componentStack ?? null });
  }

  handleRetry = () => {
    this.props.onReset();
    this.setState(CLEARED);
  };

  render() {
    const { error, eventId, componentStack, recovering } = this.state;
    if (!error) return this.props.children;
    // A stale chunk never renders the crash card — not even once automatic
    // recovery is used up, when the cause is almost always the connection.
    if (isChunkLoadError(error)) {
      return <ChunkRecoveryScreen failed={!recovering} />;
    }

    return (
      <RouteErrorFallback
        error={error}
        eventId={eventId}
        componentStack={componentStack}
        onRetry={this.handleRetry}
        chrome={this.props.chrome}
      />
    );
  }
}

export interface RouteErrorBoundaryProps {
  children: ReactNode;
  /**
   * Defaults to `shell`. Use `page` on routes that render outside the
   * dashboard (auth screens, public pages) — there is no shell to fall back
   * into — and `inline` when a layout above has already painted one.
   */
  chrome?: ErrorChrome;
}

export function RouteErrorBoundary({
  children,
  chrome = 'shell',
}: RouteErrorBoundaryProps) {
  const { pathname } = useLocation();
  const { reset } = useQueryErrorResetBoundary();

  // A print sheet or the waiting-room wall has no shell, whatever the caller
  // asked for — rendering one there would be worse than the error.
  const resolved: ErrorChrome =
    chrome === 'shell' && isChromelessPath(pathname) ? 'page' : chrome;

  return (
    <RouteErrorBoundaryInner
      resetKey={pathname}
      chrome={resolved}
      onReset={reset}
    >
      {children}
    </RouteErrorBoundaryInner>
  );
}
